/**
 * TvGuideAdminTab — admin controls for the TAP IN TV Guide.
 *
 * Three sections:
 *   1. Hero banner — set image OR video, title, subtitle, CTA, LIVE pill.
 *   2. Shows — CRUD per-show (channel, title, tagline, recurring time, art).
 *   3. Featured rail — drag/drop or click-to-pick the 6 cards at the bottom.
 *
 * Public reads come from /api/tv-guide/*, writes go to /api/admin/tv-guide/*.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Tv, Image as ImageIcon, Video as VideoIcon, Save, Trash2, Plus, Star, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function TvGuideAdminTab() {
  return (
    <div data-testid="tv-guide-admin-tab" className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold flex items-center gap-2"><Tv className="w-3.5 h-3.5" /> TV Guide</div>
        <h2 className="font-display font-bold text-2xl text-white">Programming control room</h2>
        <p className="text-white/55 text-sm mt-0.5">Edit the hero banner, shows, and featured rail. Times shown in the viewer's local timezone.</p>
      </header>
      <HeroSection />
      <ShowsSection />
      <FeaturedSection />
    </div>
  );
}

/* ─── Hero section ─── */
function HeroSection() {
  const [hero, setHero] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/tv-guide/hero").then((r) => setHero(r.data || {})).catch(() => setHero({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/tv-guide/hero", {
        title: hero.title || "",
        subtitle: hero.subtitle || "",
        kind: hero.kind || "image",
        url: hero.url || "",
        poster_url: hero.poster_url || "",
        cta_label: hero.cta_label || "Watch Now",
        cta_url: hero.cta_url || "",
        live_label: hero.live_label || "LIVE NOW",
        is_live: !!hero.is_live,
      });
      toast.success("Hero saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!hero) return null;
  const Field = ({ k, label, type = "text", placeholder = "" }) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-white/50 font-black">{label}</span>
      <input
        type={type}
        data-testid={`tv-hero-${k}`}
        value={hero[k] || ""}
        onChange={(e) => setHero({ ...hero, [k]: e.target.value })}
        placeholder={placeholder}
        className="w-full mt-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-red-400"
      />
    </label>
  );

  return (
    <section className="bg-black/40 border border-white/10 rounded-2xl p-5 space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display font-bold text-lg">Hero banner</h3>
        <span className="text-white/40 text-xs">Renders top-right of /tv-guide</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Field k="title" label="Title" placeholder="TAP IN TALK" />
          <Field k="subtitle" label="Subtitle / pitch" placeholder="Real conversations…" />
          <div className="grid grid-cols-2 gap-3">
            <Field k="cta_label" label="CTA label" placeholder="Watch Now" />
            <Field k="cta_url"   label="CTA URL"   placeholder="/show/tap-in-talk" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-white/70 inline-flex items-center gap-2">
              <input
                data-testid="tv-hero-kind"
                type="checkbox"
                checked={hero.kind === "video"}
                onChange={(e) => setHero({ ...hero, kind: e.target.checked ? "video" : "image" })}
              /> Use video instead of image
            </label>
            <label className="text-xs text-white/70 inline-flex items-center gap-2">
              <input
                data-testid="tv-hero-is-live"
                type="checkbox"
                checked={!!hero.is_live}
                onChange={(e) => setHero({ ...hero, is_live: e.target.checked })}
              /> Show LIVE NOW pill
            </label>
          </div>
          <Field k="url" label={hero.kind === "video" ? "Video URL (mp4 / webm)" : "Image URL"} placeholder="https://…" />
          {hero.kind === "video" && <Field k="poster_url" label="Video poster fallback" placeholder="https://…" />}
        </div>
        <div className="rounded-xl overflow-hidden border border-white/10 bg-black aspect-video relative">
          {hero.kind === "video" && hero.url
            ? <video src={hero.url} poster={hero.poster_url} muted autoPlay loop className="w-full h-full object-cover" />
            : hero.url
              ? <img src={hero.url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">Preview appears here</div>}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
          <div className="absolute inset-0 p-4 flex flex-col justify-center">
            {hero.is_live && <span className="bg-red-600 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded w-fit mb-2">LIVE NOW</span>}
            <div className="font-display font-black text-2xl uppercase">{hero.title || "Title"}</div>
            <div className="text-white/70 text-xs mt-1 max-w-xs">{hero.subtitle || "Subtitle"}</div>
          </div>
        </div>
      </div>

      <button data-testid="tv-hero-save" onClick={save} disabled={busy} className="bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-black text-sm px-5 py-2 rounded-lg inline-flex items-center gap-2">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save hero"}
      </button>
    </section>
  );
}

/* ─── Shows section ─── */
function ShowsSection() {
  const [shows, setShows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/tv-guide/shows").then((r) => setShows(r.data || [])).catch(() => setShows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = () => { load(); setEditing(null); setCreating(false); };

  return (
    <section className="bg-black/40 border border-white/10 rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display font-bold text-lg">Shows</h3>
        <button data-testid="tv-shows-add" onClick={() => setCreating(true)} className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> New show
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="tv-shows-table">
          <thead className="text-white/40 text-[10px] uppercase tracking-wider">
            <tr><th className="text-left py-2 px-2">Channel</th><th className="text-left py-2 px-2">Show</th><th className="text-left py-2 px-2 hidden md:table-cell">When</th><th className="text-right py-2 px-2">Edit</th></tr>
          </thead>
          <tbody>
            {shows.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="py-2 px-2 text-white/60 tabular-nums">{s.channel_number}</td>
                <td className="py-2 px-2 text-white font-bold">{s.title}</td>
                <td className="py-2 px-2 text-white/60 hidden md:table-cell">{Math.floor(s.recurring_start_h)}:{String(Math.round((s.recurring_start_h % 1) * 60)).padStart(2, "0")} → {Math.floor(s.recurring_end_h)}:{String(Math.round((s.recurring_end_h % 1) * 60)).padStart(2, "0")}</td>
                <td className="py-2 px-2 text-right">
                  <button data-testid={`tv-show-edit-${s.slug}`} onClick={() => setEditing(s)} className="text-tv-accent hover:text-white text-xs font-bold underline-offset-2 hover:underline">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editing || creating) && (
        <ShowEditor
          show={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={onSaved}
        />
      )}
    </section>
  );
}

function ShowEditor({ show, onClose, onSaved }) {
  const [s, setS] = useState(show || {
    title: "", channel_number: 101, description: "", tagline: "",
    category: "talk", hero_url: "", poster_url: "",
    recurring_start_h: 20, recurring_end_h: 21, weekday_mask: 0b1111111,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        title: s.title,
        channel_number: parseInt(s.channel_number, 10),
        description: s.description || "",
        tagline: s.tagline || "",
        category: s.category || "",
        hero_url: s.hero_url || "",
        poster_url: s.poster_url || "",
        recurring_start_h: parseFloat(s.recurring_start_h) || 0,
        recurring_end_h: parseFloat(s.recurring_end_h) || 0,
        weekday_mask: parseInt(s.weekday_mask, 10) || 0b1111111,
      };
      if (show?.slug) {
        await api.put(`/admin/tv-guide/shows/${show.slug}`, body);
      } else {
        await api.post("/admin/tv-guide/shows", body);
      }
      toast.success("Show saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!show?.slug) return;
    if (!window.confirm(`Delete "${show.title}"?`)) return;
    try {
      await api.delete(`/admin/tv-guide/shows/${show.slug}`);
      toast.success("Show deleted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="tv-show-editor" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 rounded-2xl p-6 max-w-xl w-full space-y-3">
        <h3 className="font-display font-bold text-lg">{show ? "Edit show" : "New show"}</h3>
        <input data-testid="tv-show-title" placeholder="Title" value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="tv-show-channel" value={s.channel_number} onChange={(e) => setS({ ...s, channel_number: e.target.value })} className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm">
            {[101, 102, 103, 104, 105].map((n) => <option key={n} value={n} className="bg-[#0c0915]">Ch {n}</option>)}
          </select>
          <select data-testid="tv-show-category" value={s.category} onChange={(e) => setS({ ...s, category: e.target.value })} className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm">
            {["talk", "streetz", "sports", "movies", "music"].map((c) => <option key={c} value={c} className="bg-[#0c0915]">{c}</option>)}
          </select>
        </div>
        <input data-testid="tv-show-tagline" placeholder="One-line tagline" value={s.tagline} onChange={(e) => setS({ ...s, tagline: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
        <textarea data-testid="tv-show-description" placeholder="Longer description" value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} rows={2} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm resize-none" />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="tv-show-poster" placeholder="Poster URL (2:3 portrait)" value={s.poster_url} onChange={(e) => setS({ ...s, poster_url: e.target.value })} className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="tv-show-hero" placeholder="Hero URL (16:9 landscape)" value={s.hero_url} onChange={(e) => setS({ ...s, hero_url: e.target.value })} className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-white/60">Start hour (24h)
            <input data-testid="tv-show-start" type="number" step="0.25" min="0" max="23.75" value={s.recurring_start_h} onChange={(e) => setS({ ...s, recurring_start_h: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="text-xs text-white/60">End hour
            <input data-testid="tv-show-end" type="number" step="0.25" min="0.25" max="24" value={s.recurring_end_h} onChange={(e) => setS({ ...s, recurring_end_h: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="flex gap-2 pt-2">
          <button data-testid="tv-show-save" onClick={save} disabled={busy} className="bg-red-600 hover:bg-red-500 text-white font-black text-sm px-4 py-2 rounded-lg flex-1 disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-white font-bold text-sm px-4 py-2 rounded-lg">Cancel</button>
          {show?.slug && <button data-testid="tv-show-delete" onClick={remove} className="bg-rose-700 hover:bg-rose-600 text-white text-sm px-3 py-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
    </div>
  );
}

/* ─── Featured section ─── */
function FeaturedSection() {
  const [shows, setShows] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/tv-guide/shows").then((r) => r.data || []),
      api.get("/tv-guide/featured").then((r) => r.data || []),
    ]).then(([all, feat]) => { setShows(all); setPicked((feat || []).map((s) => s.id)); });
  };
  useEffect(load, []);

  const toggle = (id) => {
    setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length < 6 ? [...p, id] : p));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/tv-guide/featured", { show_ids: picked });
      toast.success(`Featured saved · ${picked.length}/6`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-black/40 border border-white/10 rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-lg">Featured shows</h3>
          <p className="text-white/50 text-xs">Pick up to 6 shows for the bottom rail. Order = pick order.</p>
        </div>
        <span data-testid="tv-featured-count" className="bg-red-600/20 text-red-300 font-black text-xs px-3 py-1 rounded-full">{picked.length}/6</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="tv-featured-picker">
        {shows.map((s) => {
          const i = picked.indexOf(s.id);
          const on = i >= 0;
          return (
            <button
              key={s.id}
              data-testid={`tv-featured-toggle-${s.slug}`}
              onClick={() => toggle(s.id)}
              className={`relative aspect-[2/3] rounded-lg overflow-hidden border-2 text-left ${on ? "border-red-500" : "border-white/10 hover:border-white/30"}`}
            >
              {s.poster_url
                ? <img src={s.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                : <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${s.accent_color || "#EF4444"}33, #000)` }} />}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-2">
                <div className="text-[10px] text-white/60 font-bold tabular-nums">CH {s.channel_number}</div>
                <div className="font-display font-black text-xs uppercase leading-tight line-clamp-2">{s.title}</div>
              </div>
              {on && <span className="absolute top-1 right-1 bg-red-600 text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center">{i + 1}</span>}
            </button>
          );
        })}
      </div>
      <button data-testid="tv-featured-save" onClick={save} disabled={busy} className="mt-4 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-black text-sm px-5 py-2 rounded-lg inline-flex items-center gap-2">
        <Save className="w-4 h-4" /> Save featured rail
      </button>
    </section>
  );
}
